import { setAccessToken } from '../utils/api.utils';
import { validateTokenExpiry, diagnoseAuthIssue } from '../utils/auth-helpers';
import { logAuthEvent } from '../utils/sentry';
import api from '../utils/api.utils';

class TokenManager {
  private refreshTimer: NodeJS.Timeout | null = null;
  private currentToken: string | null = null;

  public setToken(token: string | null) {
    this.currentToken = token;
    setAccessToken(token);
    
    if (token) {
      this.scheduleRefresh(token);
      logAuthEvent('token_set', { hasToken: !!token });
    } else {
      this.clearRefreshTimer();
      logAuthEvent('token_cleared');
    }
  }

  private scheduleRefresh(token: string) {
    this.clearRefreshTimer();
    
    const validation = validateTokenExpiry(token);
    
    if (!validation.isValid) {
      logAuthEvent('invalid_token_on_schedule', validation);
      return;
    }

    if (validation.timeRemaining) {
      // Schedule refresh 1 minute before expiry, minimum 30 seconds
      const refreshIn = Math.max((validation.timeRemaining - 60) * 1000, 30000);
      
      logAuthEvent('refresh_scheduled', { 
        refreshInSeconds: refreshIn / 1000,
        timeRemaining: validation.timeRemaining 
      });
      
      this.refreshTimer = setTimeout(async () => {
        await this.refreshToken();
      }, refreshIn);
    }
  }

  private async refreshToken(): Promise<boolean> {
    try {
      logAuthEvent('proactive_refresh_attempt');
      
      const response = await api.post('/api/v1/auth/refresh');
      const newToken = response.data.access_token;
      
      if (newToken) {
        this.setToken(newToken);
        logAuthEvent('proactive_refresh_success');
        return true;
      }
      
      logAuthEvent('proactive_refresh_no_token');
      return false;
    } catch (error: any) {
      logAuthEvent('proactive_refresh_failed', { 
        error: error.response?.data?.message,
        status: error.response?.status
      });
      
      // If refresh fails, diagnose the issue
      diagnoseAuthIssue();
      
      // Don't redirect here - let the main interceptor handle it
      return false;
    }
  }

  private clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  public getCurrentToken(): string | null {
    return this.currentToken;
  }

  public validateCurrentToken(): boolean {
    if (!this.currentToken) return false;
    
    const validation = validateTokenExpiry(this.currentToken);
    return validation.isValid;
  }

  public destroy() {
    this.clearRefreshTimer();
    this.currentToken = null;
  }
}

export const tokenManager = new TokenManager();